import { Module } from 'vuex';

import axios from 'axios';
import { DateTime } from 'luxon';
import { ContactGroupMember, Message, Patient, PatientEvent, PatientSubscription } from './classes';

export interface PatientModuleState {
    patient: Patient;
    messages: Message[];
    scheduledMessages: Message[];
    subscriptions: PatientSubscription[];
    memberships: ContactGroupMember[];
}

export const PatientModule: Module<PatientModuleState, object> = {
    namespaced: true,
    state: {
        patient: {} as Patient,
        messages: [],
        scheduledMessages: [],
        subscriptions: [],
        memberships: []
    },
    getters: { },
    mutations: {
        setPatient(state, patient: Patient) {
            state.patient = patient;
        },
        setPatientMessages(state, messages: Message[]) {
            state.messages = messages;
        },
        setScheduledMessages(state, scheduledMessages: Message[]) {
            state.scheduledMessages = scheduledMessages;
        },
        setSubscriptions(state, subscriptions: PatientSubscription[]) {
            state.subscriptions = subscriptions;
        },
        setMemberships(state, memberships: ContactGroupMember[]) {
            state.memberships = memberships;
        },
    },
    actions: {
        clearPatient({commit}): void {
            commit('setPatient', {});
            commit('setPatientMessages', []);
        },
        async loadPatient({commit, dispatch}, patientId: number): Promise<boolean> {
            try {
                const [patient, patientMessages, events] = await Promise.all([
                    (await axios.get<Patient>(`/patients/${patientId}`)).data,
                    (await axios.get<Message[]>(`/patients/${patientId}/messages`)).data,
                    (await axios.get<PatientEvent[]>(`/patients/${patientId}/events`)).data,
                    dispatch('loadScheduledMessages', patientId),
                    dispatch('loadSubscriptions', patientId),
                    dispatch('loadMemberships', patientId),
                ]);

                commit('setPatient', patient);

                // collect events that happened before each message
                const messages = patientMessages.map(msg => {
                    msg.patientEvents = [];
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    while (events.length > 0 && DateTime.fromISO(msg.dateProcessed!) > DateTime.fromISO(events[0].eventDate || events[0].dateCreated)) {
                        msg.patientEvents.push(events.shift() as PatientEvent);
                    }
                    return msg;
                }) ?? [];
                // collect remaining events at end
                if (events.length) {
                    messages.push({ patientEvents: events, isPatientEventsMessage: true } as Message);
                }
                commit('setPatientMessages', messages);
            } catch (e) {
                return false;
            }
            return true;
        },
        async loadSubscriptions({commit, state}, patientId?: number): Promise<PatientSubscription[]> {
            const subscriptions = (await axios.get<PatientSubscription[]>(`/patients/${patientId || state.patient?.id}/subscriptions`)).data;
            commit('setSubscriptions', subscriptions)
            return subscriptions;
        },
        async loadMemberships({commit, state}, patientId?: number): Promise<ContactGroupMember[]> {
            const memberships = (await axios.get<ContactGroupMember[]>(`/patients/${patientId || state.patient?.id}/memberships`)).data;
            commit('setMemberships', memberships)
            return memberships;
        },
        async loadScheduledMessages({commit, state}, patientId?: number): Promise<Message[]> {
            const scheduledMessages = (await axios.get<Message[]>(`/patients/${patientId || state.patient?.id}/messages/scheduled`)).data;
            commit('setScheduledMessages', scheduledMessages)
            return scheduledMessages;
        },
    },
    modules: { }
};
